import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo } from 'react';
import { useHasNewCarTile } from '~/components/srp/Banners/PromoBanner/hooks/useHasNewCarTile';
import { SEBS_COMPOSER_EXPERIMENT } from '~/constants/composer';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/srp/experiments';
import { useSelectedTrade } from '~/context/common/TradeContext';
import { useUserIsImpersonating } from '~/context/common/UserContext';
import {
  useComposerAssignedBucket,
  useShowComposerTreatment
} from '~/context/composer/ComposerContext';
import {
  useDealershipId,
  useIsDealershipPage
} from '~/context/dealership/StaticContext';
import { useFinanceInfo } from '~/context/srp/FinanceContext';
import useEventExperiments from '~/hooks/common/telemetry/useEventExperiments';
import useSendFeatureBranchToDataDog from '~/hooks/common/telemetry/useSendFeatureBranchToDataDog';
import useSendNativeDeviceTypeToDataDog from '~/hooks/common/telemetry/useSendNativeDeviceTypeToDataDog';

export default function useSetDataDogContext() {
  const financeInfo = useFinanceInfo();
  const tradeInfo = useSelectedTrade();
  const composerAssignedBucket = useComposerAssignedBucket();
  const showComposerTreatment = useShowComposerTreatment();
  const isImpersonating = useUserIsImpersonating();
  const dealershipId = useDealershipId();
  const isDealershipPage = useIsDealershipPage();
  const hasNewCarConfiguratorTile = useHasNewCarTile();

  const composerExperiments = useMemo(
    () => ({ [SEBS_COMPOSER_EXPERIMENT]: composerAssignedBucket }),
    [composerAssignedBucket]
  );

  useSendFeatureBranchToDataDog();
  useSendNativeDeviceTypeToDataDog();
  useEventExperiments(
    browserCookieIdExperiments,
    userIdExperiments,
    composerExperiments
  );

  useEffect(() => {
    datadogRum.setGlobalContextProperty('isImpersonating', isImpersonating);
    datadogRum.setGlobalContextProperty(
      'hasNewCarConfiguratorTile',
      hasNewCarConfiguratorTile
    );
    datadogRum.setGlobalContextProperty(
      'softpullStatus',
      financeInfo?.financingType
    );
    datadogRum.setGlobalContextProperty(
      'tradeStatus',
      tradeInfo?.lifeCycleState
    );
    datadogRum.setGlobalContextProperty('dealershipId', dealershipId);
    datadogRum.setGlobalContextProperty('isDealershipPage', isDealershipPage);
    datadogRum.setGlobalContextProperty(
      'showComposerTreatment',
      showComposerTreatment
    );
  }, [
    dealershipId,
    financeInfo?.financingType,
    hasNewCarConfiguratorTile,
    isDealershipPage,
    isImpersonating,
    showComposerTreatment,
    tradeInfo?.lifeCycleState
  ]);
}
